import * as React from "react";
import { cn } from "../../lib/utils";

// A filled well (`docs/borderless-contract.md` §1): the fill is the affordance,
// the ring is the focus signal. `bg-field` is `bg-sunken` by default and is
// translucent there, so the same class reads correctly on the canvas and on a
// raised panel — an app that re-points the control fill (opero-app does) moves
// this, `Textarea` and the `field` trigger together and nothing else.
const inputBaseClass = [
  "flex w-full rounded-md bg-field",
  "px-4 text-sm text-text placeholder:text-text-tertiary",
  "transition-[background-color] duration-100 ease-out-fast",
  "hover:bg-field-strong",
  "focus-visible:outline-none focus-fill",
  "disabled:cursor-not-allowed disabled:opacity-50",
  "file:bg-transparent file:text-xs file:font-medium",
].join(" ");

const inputSizeClass = {
  sm: "h-control",
  default: "h-control-lg",
  // Height from the line box plus whatever block padding the caller supplies,
  // instead of a fixed control step. For a field that must occupy exactly the
  // box some *other* element occupied — `InlineEdit`, whose editor has to be
  // the same height as the prose it replaces at any type scale. A fixed
  // `h-control` cannot do that: it is 28px whether the text is 12px or 20px.
  auto: "h-auto",
} as const;

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  inputSize?: keyof typeof inputSizeClass;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputSize = "default", ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputBaseClass, inputSizeClass[inputSize], className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input, inputBaseClass, inputSizeClass };
